"use client";

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PlacesAutocomplete from "react-places-autocomplete";
import { MapPin, ArrowRight } from "lucide-react";
import { useGoogleMaps } from "@/hooks/useGoogleMaps";
import { getLogger } from "@/utils/logger";

const log = getLogger("JVAddressBox");

export default function JVAddressBox({ ctaLabel = "Get My Offer", idPrefix = "jv-address-box" }) {
  const [address, setAddress] = useState("");
  const { loaded: mapsLoaded, error: mapsError } = useGoogleMaps();
  const navigate = useNavigate();

  const handleSubmit = (event) => {
    event.preventDefault();
    const trimmedAddress = address.trim();
    if (!trimmedAddress) return;

    log.info(
      `[JVAddressBox:handleSubmit] > [Request]: address entered > [Response]: navigating to /sell`
    );
    navigate("/sell", { state: { address: trimmedAddress } });
  };

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-xl">
      <div className="flex flex-col sm:flex-row items-stretch gap-2 sm:gap-0 rounded-xl sm:rounded-full bg-white p-2 shadow-xl ring-1 ring-black/5">
        <div className="relative flex flex-1 min-w-0 items-center px-3">
          <MapPin className="h-5 w-5 shrink-0 text-[#C1694F]" />
          {mapsLoaded && !mapsError ? (
            <PlacesAutocomplete value={address} onChange={setAddress} onSelect={setAddress}>
              {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                <div className="relative w-full">
                  <input
                    {...getInputProps({ placeholder: "Enter your property address..." })}
                    className="w-full border-0 bg-transparent px-3 py-3 text-sm text-[#16213E] placeholder:text-slate-400 focus:outline-none focus:ring-0 sm:text-base"
                    id={`${idPrefix}-input-address`}
                  />
                  {(loading || suggestions.length > 0) && (
                    <div className="absolute left-0 right-0 top-full z-20 mt-1 overflow-hidden rounded-lg bg-white text-left shadow-xl ring-1 ring-black/5">
                      {loading && (
                        <div className="px-4 py-2 text-sm text-slate-500">Searching...</div>
                      )}
                      {suggestions.map((suggestion) => (
                        <div
                          key={suggestion.placeId}
                          {...getSuggestionItemProps(suggestion, {
                            className: `px-4 py-2 text-sm cursor-pointer ${
                              suggestion.active ? "bg-[#F5F3EF] text-[#16213E]" : "text-slate-600"
                            }`,
                          })}
                        >
                          {suggestion.description}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </PlacesAutocomplete>
          ) : (
            <input
              value={address}
              onChange={(event) => setAddress(event.target.value)}
              placeholder={mapsError ? "Enter address manually..." : "Loading address search..."}
              className="w-full border-0 bg-transparent px-3 py-3 text-sm text-[#16213E] placeholder:text-slate-400 focus:outline-none focus:ring-0 sm:text-base"
              id={`${idPrefix}-input-address-fallback`}
            />
          )}
        </div>
        <button
          type="submit"
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#C1694F] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#a8563f] sm:rounded-full sm:text-base"
          id={`${idPrefix}-btn-submit`}
        >
          {ctaLabel}
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </form>
  );
}
